// Components/FloatingOrderButton.js
import './style.css';
import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';

// Estilo do modal
const trackingModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 500,
  bgcolor: 'var(--background)',
  border: '2px solid var(--title)',
  boxShadow: 24,
  p: 3,
  borderRadius: '16px',
  maxHeight: '80vh',
  overflowY: 'auto',
};

// Timeline do pedido
const OrderTimeline = ({ status }) => {
  const steps = [
    { key: 'preparing', label: 'Preparando', icon: <RestaurantIcon /> },
    { key: 'ready', label: 'Pronto', icon: <LocalBarIcon /> },
    { key: 'delivered', label: 'Entregue', icon: <DeliveryDiningIcon /> },
  ];

  return (
    <div className="order-timeline">
      {steps.map((step, index) => {
        const isCompleted = steps.findIndex((s) => s.key === status) >= index;
        const isCurrent = step.key === status;
        return (
          <div
            key={step.key}
            className={`timeline-step ${isCompleted ? 'completed' : ''} ${
              isCurrent ? 'current' : ''
            }`}
          >
            <div className="timeline-icon">
              {isCompleted ? <CheckCircleIcon /> : step.icon}
            </div>
            <div className="timeline-label">{step.label}</div>
            {index < steps.length - 1 && <div className="timeline-connector" />}
          </div>
        );
      })}
    </div>
  );
};

// 🧹 Remove pedidos expirados
const cleanExpiredOrders = (orders) => {
  const now = new Date();
  return orders.filter((order) => {
    if (!order.expiresAt) return true;
    return new Date(order.expiresAt) > now;
  });
};

const FloatingOrderButton = ({ themeColor, textColor }) => {
  const [activeOrders, setActiveOrders] = useState([]);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);

  // 🔁 Atualiza lista de pedidos ativos (com expiração real)
  const refreshActiveOrders = () => {
    try {
      const ordersData = localStorage.getItem('activeOrders');

      if (!ordersData) {
        setActiveOrders([]);
        return;
      }

      let orders = JSON.parse(ordersData);

      // 🧹 Limpa expirados
      orders = cleanExpiredOrders(orders);

      const now = new Date();

      const validOrders = orders.filter((order) => {
        const completedAt = order.completedAt
          ? new Date(order.completedAt)
          : null;

        if (order.status === 'delivered' && completedAt) {
          const diff = (now - completedAt) / (1000 * 60);
          return diff < 30; // mantém entregues por até 30min
        }

        const createdAt = new Date(order.createdAt || order.timestamp);
        return (now - createdAt) / (1000 * 60 * 60) < 6; // até 6h de vida
      });

      setActiveOrders(validOrders);

      // 💾 Atualiza storage se mudou
      if (validOrders.length !== orders.length) {
        localStorage.setItem('activeOrders', JSON.stringify(validOrders));
      }
    } catch (err) {
      console.error('Erro ao atualizar pedidos ativos:', err);
      localStorage.removeItem('activeOrders');
      setActiveOrders([]);
    }
  };

  // ⏱ Atualiza a cada 10s
  useEffect(() => {
    refreshActiveOrders();
    const interval = setInterval(refreshActiveOrders, 10000);
    return () => clearInterval(interval);
  }, []);

  // 🔄 Atualiza quando storage mudar
  useEffect(() => {
    const handleStorageChange = () => refreshActiveOrders();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // --- Formatações auxiliares ---
  const formatTime = (minutes) =>
    minutes <= 0 ? 'Expirado' : `${minutes} min`;

  const getStatusText = (status) => {
    switch (status) {
      case 'preparing':
        return 'Preparando';
      case 'ready':
        return 'Pronto para entrega';
      case 'delivered':
        return 'Entregue';
      default:
        return 'Preparando';
    }
  };

  // ❌ Não mostra botão se não houver pedidos válidos
  if (!activeOrders.length) return null;

  return (
    <>
      {/* Botão flutuante */}
      {/* <button
        className="floating-tracking-btn"
        onClick={() => setTrackingModalOpen(true)}
        style={{
          backgroundColor: themeColor || '#4892DF',
          color: textColor || 'white',
        }}
      >
        <AccessTimeIcon />
        <span style={{ color: '#fff' }}>
          {activeOrders.length > 1
            ? `${activeOrders.length} Pedidos`
            : 'Acompanhar Pedido'}
        </span>
      </button> */}

      {/* Modal de acompanhamento */}
      <Modal
        open={trackingModalOpen}
        onClose={() => setTrackingModalOpen(false)}
        aria-labelledby="tracking-modal-title"
      >
        <Box sx={trackingModalStyle}>
          <div className="tracking-modal-container">
            <h2 className="tracking-title">
              {activeOrders.length > 1
                ? 'Seus Pedidos Ativos'
                : 'Acompanhe seu Pedido'}
            </h2>

            {activeOrders.map((order, i) => (
              <div key={order.id} className="order-container">
                {activeOrders.length > 1 && (
                  <div className="order-header-multiple">
                    <h3>Pedido #{i + 1}</h3>
                    <span className="order-badge">
                      {formatTime(order.timeRemaining || 30)}
                    </span>
                  </div>
                )}

                <div className="order-summary">
                  <div className="order-header">
                    {activeOrders.length === 1 && (
                      <h3>Pedido #{order.id.slice(-4)}</h3>
                    )}
                    <p className="order-time">
                      Realizado às{' '}
                      {new Date(order.timestamp).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                    <p className="order-status-text">
                      Status: {getStatusText(order.status)}
                    </p>
                  </div>

                  <div className="order-items">
                    {order.items?.map((item, index) => (
                      <div key={index} className="order-item">
                        <span className="item-name">{item.nome || 'Item'}</span>
                        <span className="item-price">
                          R${' '}
                          {parseFloat(item.valor || 0).toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    ))}
                    <div className="order-total">
                      <strong>
                        Total: R${' '}
                        {parseFloat(order.total || 0).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </strong>
                    </div>
                  </div>
                </div>

                <OrderTimeline status={order.status} />

                {activeOrders.length === 1 && (
                  <div className="estimated-time">
                    <AccessTimeIcon />
                    <span>
                      {order.status === 'preparing'
                        ? `Preparando seu pedido (${
                            order.timeRemaining || 30
                          } min restantes)`
                        : order.status === 'ready'
                          ? `Pedido pronto para entrega (${
                              order.timeRemaining || 10
                            } min restantes)`
                          : 'Pedido entregue!'}
                    </span>
                  </div>
                )}

                {i < activeOrders.length - 1 && (
                  <hr className="order-divider" />
                )}
              </div>
            ))}

            <button
              className="close-tracking-btn"
              onClick={() => setTrackingModalOpen(false)}
              style={{
                backgroundColor: themeColor || '#4892DF',
                color: textColor || 'white',
              }}
            >
              Fechar
            </button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default FloatingOrderButton;
